import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const getSessionId = () => {
  let id = sessionStorage.getItem("flavios_chat_session");
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem("flavios_chat_session", id);
  }
  return id;
};

const WELCOME = {
  role: "assistant",
  content:
    "Hi! I'm Flavio's virtual assistant. Ask me about prices, hours, or describe your hair and I'll suggest the perfect service for you.",
};

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "client", content: text }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: text }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = dec.decode(value);
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = {
            role: "assistant",
            content: copy[copy.length - 1].content + chunk,
          };
          return copy;
        });
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "Sorry, something went wrong — please call us at (905) 822-2690.",
        };
        return copy;
      });
    }
    setBusy(false);
  };

  return (
    <>
      <button
        data-testid="chat-widget-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-label="Chat with us"
        className="fixed bottom-5 left-4 lg:left-auto lg:right-4 z-50 w-14 h-14 rounded-full bg-gold text-ink-900 flex items-center justify-center shadow-2xl shadow-black/30 hover:scale-105 transition-transform duration-300"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed bottom-24 left-4 right-4 sm:left-auto sm:right-4 sm:w-[380px] z-50 bg-sand-50 border border-black/10 shadow-2xl shadow-black/20 flex flex-col overflow-hidden"
          style={{ height: "min(520px, 70vh)" }}
        >
          <div className="bg-ink-900 text-sand-50 px-5 py-4 flex items-center gap-3">
            <Sparkles className="w-4 h-4 text-gold" />
            <div>
              <p className="font-serif text-lg leading-none">Flavio&apos;s Assistant</p>
              <p className="text-[0.6rem] tracking-[0.2em] uppercase text-sand-300/70 mt-1">
                Prices · Hours · Hair Advice
              </p>
            </div>
          </div>

          <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chat-msg-${m.role}-${i}`}
                className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "client"
                    ? "ml-auto bg-ink-900 text-sand-50"
                    : "mr-auto bg-sand-100 text-ink-900 border border-black/5"
                }`}
              >
                {m.content || (
                  <span className="inline-flex gap-1 items-center text-ink-500">
                    <span className="w-1.5 h-1.5 bg-gold rounded-full animate-bounce" />
                    <span className="w-1.5 h-1.5 bg-gold rounded-full animate-bounce [animation-delay:120ms]" />
                    <span className="w-1.5 h-1.5 bg-gold rounded-full animate-bounce [animation-delay:240ms]" />
                  </span>
                )}
              </div>
            ))}
          </div>

          <div className="border-t border-black/10 p-3 flex items-center gap-2 bg-sand-50">
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask about prices, hours, your hair…"
              className="flex-1 bg-sand-100 border border-black/10 px-4 py-3 text-sm text-ink-900 placeholder:text-ink-500/60 focus:outline-none focus:border-gold"
            />
            <button
              data-testid="chat-send-btn"
              onClick={send}
              disabled={busy || !input.trim()}
              aria-label="Send"
              className="w-11 h-11 bg-ink-900 text-sand-50 flex items-center justify-center hover:bg-gold hover:text-ink-900 transition-colors disabled:opacity-40"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
